import { FC, useState } from 'react';

import { t } from '@core/i18n';
import { withStyles } from '@core/theme/utils/with-styles';
import { Switch, SwitchSize, SwitchType } from '@shared/components/switch';
import { ChevronDownIcon } from '@shared/icons/chevron-down';
import { ChevronUpIcon } from '@shared/icons/chevron-up';
import { Settings2Icon } from '@shared/icons/settings-2';
import { colours } from '@shared/theme/colours';

import { styles } from './FieldToggleList.styles';
import { FieldToggleListProps } from './FieldToggleList.types';

const normalizeBoolean = (value: boolean | string | undefined): boolean => {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.toLowerCase() !== 'false';
  }
  return value !== false;
};

const FieldToggleListComponent: FC<FieldToggleListProps> = ({
  classes,
  fieldVisibility,
  onFieldToggle,
  fieldList = [],
  collapsible = true,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleToggleChange = (fieldKey: string) => (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    onFieldToggle(fieldKey, checked);
  };

  return (
    <div className={collapsible ? classes.root : classes.rootNoBorder}>
      <div className={classes.headerContainer}>
        <div className={classes.titleSection}>
          <Settings2Icon color={colours.neutral[400]} classes={{ root: classes.settingsIcon }} />
          <span className={classes.headerTitle}>{t('manage_what_data_is_shared')}</span>
          {collapsible && (
            <div className={classes.chevronIcon} onClick={() => setIsCollapsed(!isCollapsed)}>
              {isCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
            </div>
          )}
        </div>
      </div>

      {(!collapsible || !isCollapsed) && (
        <>
          <div className={classes.fieldsContainer}>
            {fieldList.map((field) => {
              const isFieldVisible = normalizeBoolean(fieldVisibility[field.key]);

              return (
                <div key={field.key} className={classes.fieldToggleRow}>
                  <div className={classes.fieldLabelContainer}>
                    <span className={classes.fieldLabel}>{field.displayName}</span>
                  </div>
                  <div className={classes.switchContainer}>
                    <Switch
                      checked={isFieldVisible}
                      color="primary"
                      size={SwitchSize.small}
                      type={SwitchType.round}
                      onChange={handleToggleChange(field.key)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export const FieldToggleList = withStyles(styles)(FieldToggleListComponent);
